import { defineStore } from 'pinia'
import type { Customer, CustomerRegistrationData, PasswordResetData } from '~/types'

export const useCustomerStore = defineStore('customer', () => {
  const customer = ref<Customer | null>(null)
  const pending = ref(false)
  const loaded = ref(false)

  const catalogService = useCatalogService()

  const login = async (email: string, password: string) => {
    try {
      pending.value = true
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate network delay
      customer.value = await catalogService.loginCustomer(email, password).then(res => res.data)
    } finally {
      pending.value = false
    }
  }

  const register = async (data : CustomerRegistrationData) => {
    try {
      pending.value = true
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate network delay
      customer.value = await catalogService.registerCustomer(data).then(res => res.data)
    } finally {
      pending.value = false
    }
  }

  const load = async () => {
    try {
      pending.value = true
      customer.value = await catalogService.getCustomerProfile().then(res => res.data)
    } catch (error) {
      console.error('Failed to load customer profile:', error)
      customer.value = null
    } finally {
      pending.value = false
      loaded.value = true
    }
  }

  const logout = async () => {
   await catalogService.logoutCustomer()
   customer.value = null
  }

  const sendPasswordResetLink = async (email: string) => {
    try {
      pending.value = true
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate network delay
      await catalogService.sendCustomerPasswordResetLink(email)
    } finally {
      pending.value = false
    }
  }

  const updateProfile = async (data: Partial<Customer>) => {
      customer.value = await catalogService.updateCustomerProfile(data).then(res => res.data)
  }

  const changeEmail = async (newEmail: string, password: string) => {
    customer.value = await catalogService.changeCustomerEmail({newEmail, password}).then(res => res.data)
  }

  const resetPassword = async (data: PasswordResetData) => {
   await catalogService.resetCustomerPassword(data)
  }

  const resendEmailVerification = async () => {
    await catalogService.resendCustomerEmailVerification()
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await catalogService.changeCustomerPassword({currentPassword, newPassword})
  }

  const deleteAccount = async (password: string) => {
    await catalogService.deleteCustomerAccount(password)
    customer.value = null
  }

  return {
    customer,
    load,
    pending,
    login,
    register,
    logout,
    sendPasswordResetLink,
    loaded,
    resetPassword,
    updateProfile,
    changeEmail,
    resendEmailVerification,
    changePassword,
    deleteAccount
  }
})
